import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getProducts } from '../services/productService';
import { getDepartments, getProductTypes, getProductUses } from '../services/catalogService';
import ProductForm from '../components/ProductForm';
import { formatCurrency } from '../utils/format';

export default function Productos() {
  const [products,   setProducts]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [query,      setQuery]      = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterType, setFilterType] = useState('');

  const [catalogDepartments, setCatalogDepartments] = useState([]);
  const [catalogProductTypes, setCatalogProductTypes] = useState([]);
  const [catalogProductUses, setCatalogProductUses] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const loadProducts = useCallback(() => {
    setLoading(true);
    setError('');
    return getProducts()
      .then(data => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setError('No se pudo cargar el listado de productos.'))
      .finally(() => setLoading(false));
  }, []);

  const loadCatalogs = useCallback(() => {
    return Promise.all([
      getDepartments().then(data => setCatalogDepartments(Array.isArray(data) ? data : [])).catch(() => {}),
      getProductTypes().then(data => setCatalogProductTypes(Array.isArray(data) ? data : [])).catch(() => {}),
      getProductUses().then(data => setCatalogProductUses(Array.isArray(data) ? data : [])).catch(() => {}),
    ]);
  }, []);

  useEffect(() => {
    loadProducts();
    loadCatalogs();
  }, [loadProducts, loadCatalogs]);

  function openCreateForm() {
    setEditingProduct(null);
    setShowForm(true);
  }

  function openEditForm(product) {
    setEditingProduct(product);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingProduct(null);
  }

  function handleSaved() {
    setShowForm(false);
    setEditingProduct(null);
    loadProducts();
  }

  const departments = useMemo(() =>
    [...new Set(products.map(p => p.department?.name).filter(Boolean))].sort(),
    [products]
  );
  const productTypes = useMemo(() =>
    [...new Set(products.map(p => p.productType?.name).filter(Boolean))].sort(),
    [products]
  );

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return products.filter(p => {
      const matchName = !q || p.name.toLowerCase().includes(q);
      const matchDept = !filterDept || p.department?.name === filterDept;
      const matchType = !filterType || p.productType?.name === filterType;
      return matchName && matchDept && matchType;
    });
  }, [products, query, filterDept, filterType]);

  function clearFilters() {
    setQuery('');
    setFilterDept('');
    setFilterType('');
  }

  const hasFilters = query || filterDept || filterType;

  return (
    <div className="page-fade" style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 22, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <p style={{ fontSize: 12, color: 'var(--orig-text-muted)', marginBottom: 3 }}>
            Catálogo general del sistema
          </p>
          <h5 style={{
            fontWeight: 800, color: 'var(--orig-text)',
            fontFamily: 'var(--orig-font-display)', letterSpacing: 0.5, fontSize: 20,
          }}>
            Búsqueda de Productos
          </h5>
        </div>
        {!showForm && (
          <button className="btn-orig btn-orig-green" onClick={openCreateForm}>
            <i className="bi bi-plus-lg" />
            Nuevo Producto
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <ProductForm
          product={editingProduct}
          departments={catalogDepartments}
          productTypes={catalogProductTypes}
          productUses={catalogProductUses}
          onCancel={closeForm}
          onSaved={handleSaved}
          onCatalogsChange={loadCatalogs}
        />
      )}

      {/* Search & Filters */}
      <div className="section-card" style={{ marginBottom: 20 }}>
        <div className="section-body" style={{ padding: '16px 20px' }}>
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-5">
              <label className="form-label">Buscar producto</label>
              <div className="search-wrap">
                <i className="bi bi-search search-icon" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nombre del producto..."
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                />
                {query && (
                  <button className="clear-btn" onClick={() => setQuery('')} title="Limpiar">
                    <i className="bi bi-x-lg" />
                  </button>
                )}
              </div>
            </div>

            <div className="col-6 col-md-3">
              <label className="form-label">Departamento</label>
              <select
                className="form-select"
                value={filterDept}
                onChange={e => setFilterDept(e.target.value)}
              >
                <option value="">Todos</option>
                {departments.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>

            <div className="col-6 col-md-3">
              <label className="form-label">Tipo de producto</label>
              <select
                className="form-select"
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
              >
                <option value="">Todos</option>
                {productTypes.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            {hasFilters && (
              <div className="col-12 col-md-1">
                <button
                  className="btn-orig btn-orig-outline"
                  onClick={clearFilters}
                  style={{ width: '100%', justifyContent: 'center', padding: '9px 10px' }}
                  title="Limpiar filtros"
                >
                  <i className="bi bi-x-circle" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="section-card">
        <div className="section-header">
          <h6>
            <i className="bi bi-list-ul" style={{ color: 'var(--orig-green)' }} />
            Resultados
          </h6>
          {!loading && (
            <span className="badge-orig badge-green">
              {filtered.length} {filtered.length === 1 ? 'producto' : 'productos'}
            </span>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--orig-text-muted)' }}>
              <i className="bi bi-arrow-repeat" style={{
                fontSize: 28, display: 'block', marginBottom: 10,
                animation: 'spin 1s linear infinite',
              }} />
              Cargando productos...
            </div>
          ) : error ? (
            <div style={{ padding: '28px 20px' }}>
              <div className="alert-orig alert-error">
                <i className="bi bi-exclamation-triangle-fill" />
                {error}
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--orig-text-muted)' }}>
              <i className="bi bi-search" style={{ fontSize: 30, display: 'block', marginBottom: 10, opacity: 0.4 }} />
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Sin resultados</div>
              <div style={{ fontSize: 12 }}>
                {hasFilters ? 'No hay productos que coincidan con los filtros.' : 'No hay productos registrados.'}
              </div>
              {hasFilters && (
                <button
                  className="btn-orig btn-orig-ghost"
                  onClick={clearFilters}
                  style={{ marginTop: 14, fontSize: 12 }}
                >
                  <i className="bi bi-x-circle" />
                  Limpiar filtros
                </button>
              )}
            </div>
          ) : (
            <table className="orig-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Nombre</th>
                  <th>Departamento</th>
                  <th>Tipo</th>
                  <th>Uso</th>
                  <th>Empaque</th>
                  <th>Costo</th>
                  <th>Precio</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id}>
                    <td style={{ color: 'var(--orig-text-muted)', fontSize: 12, fontFamily: 'monospace' }}>
                      {p.id}
                    </td>
                    <td style={{ fontWeight: 500 }}>{p.name}</td>
                    <td>
                      {p.department?.name ? (
                        <span style={{
                          background: 'var(--orig-green-dim)', color: 'var(--orig-green-bright)',
                          padding: '2px 9px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                        }}>
                          {p.department.name}
                        </span>
                      ) : <span style={{ color: 'var(--orig-text-hint)' }}>—</span>}
                    </td>
                    <td>
                      {p.productType?.name ? (
                        <span style={{
                          background: 'var(--orig-teal-dim)', color: 'var(--orig-teal-light)',
                          padding: '2px 9px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                        }}>
                          {p.productType.name}
                        </span>
                      ) : <span style={{ color: 'var(--orig-text-hint)' }}>—</span>}
                    </td>
                    <td style={{ fontSize: 12.5 }}>{p.productUse?.name || <span style={{ color: 'var(--orig-text-hint)' }}>—</span>}</td>
                    <td style={{ fontSize: 12 }}>
                      <span style={{ color: 'var(--orig-amber-light)', fontWeight: 500 }}>{p.packageType}</span>
                      {p.packageQuantity && <span style={{ color: 'var(--orig-text-muted)' }}> × {p.packageQuantity}</span>}
                    </td>
                    <td style={{ fontSize: 12.5, color: 'var(--orig-text-muted)' }}>{formatCurrency(p.cost)}</td>
                    <td style={{ fontSize: 12.5, fontWeight: 500 }}>{formatCurrency(p.price)}</td>
                    <td>
                      <span className={`badge-orig ${p.state ? 'badge-green' : 'badge-red'}`}>
                        <i className="bi bi-circle-fill" style={{ fontSize: 6 }} />
                        {p.state ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn-orig btn-orig-outline"
                        onClick={() => openEditForm(p)}
                        style={{ padding: '5px 12px', fontSize: 12 }}
                        title="Editar producto"
                      >
                        <i className="bi bi-pencil" />
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
